import React, { createContext, useContext, useState, useEffect } from "react";

/**
 * Canvas integration for Zapier Interfaces
 *
 * This module handles iframe communication when Interfaces is embedded in Canvas.
 * When Canvas wants to focus on an interface, it sends a message that hides
 * the surrounding UI (header, sidebar, navigation) for a cleaner embedded experience.
 */

interface CanvasContextType {
  isRenderedInCanvas: boolean;
}

interface IframeReadyMessage {
  event: "iframe-ready";
}

interface MessageData {
  [key: string]: unknown;
  type: string;
}

const isMessageData = (data: unknown): data is MessageData =>
  typeof data === "object" && data !== null && "type" in data;

const CanvasContext = createContext<CanvasContextType | undefined>(undefined);

/**
 * Provider that manages Canvas integration state and iframe communication.
 *
 * Handles:
 * - Listening for Canvas focus mode messages
 * - Sending iframe ready signals to parent
 * - Managing embedded vs standalone UI state
 */
export function CanvasFocusProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isRenderedInCanvas, setIsRenderedInCanvas] = useState(false);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (
        isMessageData(event.data) &&
        event.data.type === "canvas:focus-mode"
      ) {
        setIsRenderedInCanvas(true);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  return (
    <CanvasContext.Provider value={{ isRenderedInCanvas }}>
      {children}
    </CanvasContext.Provider>
  );
}

/**
 * Hook to check if the interface is currently rendered in Canvas focus mode.
 *
 * When true, the interface should hide surrounding UI elements (header, sidebar, etc.)
 * to provide a cleaner embedded experience within Canvas.
 *
 * @returns {boolean} isRenderedInCanvas - Whether the interface is in Canvas focus mode
 */
export function useIsRenderedInCanvas() {
  const context = useContext(CanvasContext);
  if (context === undefined) {
    throw new Error(
      "useIsRenderedInCanvas must be used within a CanvasFocusProvider"
    );
  }
  return context;
}

/**
 * Hook that sends an iframe ready signal to the parent window when called.
 *
 * This signals to Canvas that the interface has finished loading and is ready
 * for interaction. Should be called when the page/component is fully rendered.
 */
export function useIframeReady() {
  useEffect(() => {
    try {
      if (window.parent && window.parent !== window) {
        const message: IframeReadyMessage = { event: "iframe-ready" };
        window.parent.postMessage(message, "*");
      }
    } catch (error) {
      console.warn("Failed to send iframe ready signal:", error);
    }
  }, []);
}
